import React, { useState, useEffect } from 'react';
import { 
  Settings, Hash, Image, Clock, Globe, Shield, 
  Sparkles, Save, Plus, Trash2, Edit2, Link2,
  AlertCircle, CheckCircle, Calendar
} from 'lucide-react';

// Ikony platform społecznościowych
const Facebook = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const Linkedin = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const Instagram = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.405a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z"/>
  </svg>
);

const Twitter = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
  </svg>
);

interface HashtagTemplate {
  id: string;
  name: string;
  tags: string[];
  category: string;
}

interface PostTemplate {
  id: string;
  name: string;
  content: string;
  hashtags: string[];
  platforms: string[];
}

interface PlatformSettings {
  platform: string;
  enabled: boolean;
  defaultHashtags: string[];
  postingTimes: string[];
  autoPost: boolean;
  accessToken?: string;
  pageId?: string;
}

const SocialMediaSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'general' | 'hashtags' | 'templates' | 'platforms' | 'schedule'>('general');
  const [saving, setSaving] = useState(false);
  
  // Stan dla ustawień ogólnych
  const [generalSettings, setGeneralSettings] = useState({
    aiEnabled: true,
    autoHashtags: true,
    watermark: false,
    watermarkText: '@plexisystem',
    defaultLanguage: 'pl',
    maxHashtags: 30,
    minHashtags: 5,
    autoTranslate: false,
    trackingEnabled: true,
    utmSource: 'social',
    utmMedium: 'post',
    utmCampaign: 'organic'
  });

  // Stan dla szablonów hashtagów
  const [hashtagTemplates, setHashtagTemplates] = useState<HashtagTemplate[]>([
    {
      id: '1',
      name: 'Produkty z plexi',
      tags: ['plexi', 'pleksi', 'pmma', 'tworzywa', 'produkcja', 'reklama', 'wycinanie', 'grawer'],
      category: 'Produkty'
    },
    {
      id: '2',
      name: 'Ekspozytory',
      tags: ['ekspozytor', 'display', 'standy', 'pos', 'reklama', 'marketing', 'sklep'],
      category: 'Produkty'
    },
    {
      id: '3',
      name: 'Branżowe',
      tags: ['b2b', 'biznes', 'przemysł', 'polska', 'pomorskie', 'gdansk', 'trojmiasto'],
      category: 'Biznes'
    }
  ]);

  // Stan dla szablonów postów
  const [postTemplates, setPostTemplates] = useState<PostTemplate[]>([
    {
      id: '1',
      name: 'Nowy produkt',
      content: '🎯 Nowość w ofercie! \n\n[NAZWA PRODUKTU]\n\n✅ [ZALETA 1]\n✅ [ZALETA 2]\n✅ [ZALETA 3]\n\n📞 Zamów już dziś: 884 042 107\n💻 www.plexisystem.pl',
      hashtags: ['nowość', 'produkt', 'oferta'],
      platforms: ['facebook', 'linkedin']
    },
    {
      id: '2',
      name: 'Case study',
      content: '💼 Realizacja dla [KLIENT]\n\n🎯 Wyzwanie: [OPIS]\n💡 Rozwiązanie: [OPIS]\n✨ Efekt: [OPIS]\n\nSprawdź, co możemy zrobić dla Ciebie!\n\n📞 884 042 107\n💻 www.plexisystem.pl',
      hashtags: ['realizacja', 'casestudy', 'portfolio'],
      platforms: ['linkedin', 'facebook']
    }
  ]);

  // Stan dla ustawień platform
  const [platformSettings, setPlatformSettings] = useState<PlatformSettings[]>([
    {
      platform: 'Facebook',
      enabled: true,
      defaultHashtags: ['plexisystem', 'plexi', 'reklama'],
      postingTimes: ['09:00', '15:00', '19:00'],
      autoPost: false,
      pageId: ''
    },
    {
      platform: 'LinkedIn',
      enabled: true,
      defaultHashtags: ['b2b', 'biznes', 'przemysł'],
      postingTimes: ['08:00', '12:00', '17:00'],
      autoPost: false
    },
    {
      platform: 'Instagram',
      enabled: true,
      defaultHashtags: ['plexisystem', 'design', 'reklama'],
      postingTimes: ['12:00', '18:00'],
      autoPost: false
    },
    {
      platform: 'Twitter',
      enabled: false,
      defaultHashtags: ['plexi', 'poland'],
      postingTimes: ['10:00', '16:00'],
      autoPost: false
    }
  ]);

  // Stan dla połączonych platform
  const [connectedPlatforms, setConnectedPlatforms] = useState<Record<string, boolean>>({});

  // Stan dla nowego szablonu hashtagów
  const [newHashtagTemplate, setNewHashtagTemplate] = useState({
    name: '',
    tags: '',
    category: 'Produkty'
  });

  // Stan dla edycji
  const [editingHashtagId, setEditingHashtagId] = useState<string | null>(null);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Zapisz ustawienia w localStorage lub wyślij do API
      localStorage.setItem('socialMediaSettings', JSON.stringify({
        general: generalSettings,
        hashtags: hashtagTemplates,
        templates: postTemplates,
        platforms: platformSettings
      }));
      
      setTimeout(() => {
        setSaving(false);
        alert('Ustawienia zostały zapisane!');
      }, 1000);
    } catch (error) {
      console.error('Błąd zapisu ustawień:', error);
      setSaving(false);
      alert('Błąd podczas zapisywania ustawień');
    }
  };

  const addHashtagTemplate = () => {
    if (!newHashtagTemplate.name || !newHashtagTemplate.tags) {
      alert('Wypełnij nazwę i tagi');
      return;
    }

    const newTemplate: HashtagTemplate = {
      id: Date.now().toString(),
      name: newHashtagTemplate.name,
      tags: newHashtagTemplate.tags.split(',').map(tag => tag.trim().replace('#', '')),
      category: newHashtagTemplate.category
    };

    setHashtagTemplates([...hashtagTemplates, newTemplate]);
    setNewHashtagTemplate({ name: '', tags: '', category: 'Produkty' });
  };

  const deleteHashtagTemplate = (id: string) => {
    setHashtagTemplates(hashtagTemplates.filter(t => t.id !== id));
  };

  // Funkcja łączenia z platformą
  const handleConnectPlatform = async (platform: string) => {
    // DEBUG: Sprawdź klucze
    console.log('LinkedIn Client ID:', import.meta.env.VITE_LINKEDIN_CLIENT_ID);
    console.log('LinkedIn Secret exists:', !!import.meta.env.VITE_LINKEDIN_CLIENT_SECRET);
    
    const redirectUri = `${window.location.origin}/social-media/oauth/callback?platform=${platform.toLowerCase()}`;
    let authUrl = '';

    switch (platform.toLowerCase()) {
      case 'facebook':
        const fbAppId = import.meta.env.VITE_FACEBOOK_APP_ID || 'YOUR_FB_APP_ID';
        const fbScope = 'pages_manage_posts,pages_read_engagement,pages_show_list,instagram_basic,instagram_content_publish';
        authUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${fbAppId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${fbScope}&response_type=code`;
        break;

      case 'linkedin':
        const linkedinClientId = import.meta.env.VITE_LINKEDIN_CLIENT_ID || 'YOUR_LINKEDIN_CLIENT_ID';
        // Używamy tylko podstawowych scopes dla OpenID Connect
        const linkedinScope = 'openid profile w_member_social';
        authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${linkedinClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${linkedinScope}`;
        break;

      case 'twitter':
        const twitterClientId = import.meta.env.VITE_TWITTER_CLIENT_ID || 'YOUR_TWITTER_CLIENT_ID';
        const twitterScope = 'tweet.read tweet.write users.read offline.access';
        
        // Generate code challenge for PKCE
        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        
        // Store code verifier in session storage
        sessionStorage.setItem('twitter_code_verifier', codeVerifier);
        
        authUrl = `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${twitterClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${twitterScope}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
        break;

      default:
        alert('Nieobsługiwana platforma');
        return;
    }

    // Otwórz okno autoryzacji
    window.location.href = authUrl;
  };

  // Helper functions for Twitter PKCE
  const generateCodeVerifier = (): string => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  const generateCodeChallenge = async (verifier: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(hash)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'facebook': return <Facebook className="w-5 h-5" />;
      case 'linkedin': return <Linkedin className="w-5 h-5" />;
      case 'instagram': return <Instagram className="w-5 h-5" />;
      case 'twitter': return <Twitter className="w-5 h-5" />;
      default: return <Globe className="w-5 h-5" />;
    }
  };

  // Wczytaj zapisane ustawienia i sprawdź połączone platformy
  useEffect(() => {
    // Wczytaj ustawienia
    const saved = localStorage.getItem('socialMediaSettings');
    if (saved) {
      try {
        const settings = JSON.parse(saved);
        if (settings.general) setGeneralSettings(settings.general);
        if (settings.hashtags) setHashtagTemplates(settings.hashtags);
        if (settings.templates) setPostTemplates(settings.templates);
        if (settings.platforms) setPlatformSettings(settings.platforms);
      } catch (error) {
        console.error('Błąd wczytywania ustawień:', error);
      }
    }

    // Sprawdź połączone platformy
    checkConnectedPlatforms();
  }, []);

  // Funkcja sprawdzająca które platformy są połączone
  const checkConnectedPlatforms = () => {
    try {
      const storedTokens = JSON.parse(localStorage.getItem('social_tokens') || '{}');
      const connected: Record<string, boolean> = {};
      
      connected['Facebook'] = !!(storedTokens.facebook?.token);
      connected['LinkedIn'] = !!(storedTokens.linkedin?.token);
      connected['Instagram'] = !!(storedTokens.instagram?.token);
      connected['Twitter'] = !!(storedTokens.twitter?.token);
      
      setConnectedPlatforms(connected);
      console.log('Connected platforms:', connected);
    } catch (error) {
      console.error('Error checking connected platforms:', error);
    }
  };

  // Odśwież status połączeń gdy wracamy do strony
  useEffect(() => {
    const handleFocus = () => {
      checkConnectedPlatforms();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  return (
    <div className="bg-zinc-800 rounded-lg shadow-xl border border-zinc-700">
      {/* Header */}
      <div className="p-6 border-b border-zinc-700">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Ustawienia Social Media
            </h2>
            <p className="text-gray-400 mt-1">Skonfiguruj domyślne ustawienia dla postów</p>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-all flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Zapisywanie...' : 'Zapisz wszystko'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-700">
        <div className="flex overflow-x-auto">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 font-medium whitespace-nowrap transition-all ${
              activeTab === 'general'
                ? 'text-blue-500 border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Settings className="inline w-4 h-4 mr-2" />
            Ogólne
          </button>
          <button
            onClick={() => setActiveTab('hashtags')}
            className={`px-6 py-3 font-medium whitespace-nowrap transition-all ${
              activeTab === 'hashtags'
                ? 'text-blue-500 border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Hash className="inline w-4 h-4 mr-2" />
            Hashtagi
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-6 py-3 font-medium whitespace-nowrap transition-all ${
              activeTab === 'templates'
                ? 'text-blue-500 border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Edit2 className="inline w-4 h-4 mr-2" />
            Szablony postów
          </button>
          <button
            onClick={() => setActiveTab('platforms')}
            className={`px-6 py-3 font-medium whitespace-nowrap transition-all ${
              activeTab === 'platforms'
                ? 'text-blue-500 border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Globe className="inline w-4 h-4 mr-2" />
            Platformy
          </button>
          <button
            onClick={() => setActiveTab('schedule')}
            className={`px-6 py-3 font-medium whitespace-nowrap transition-all ${
              activeTab === 'schedule'
                ? 'text-blue-500 border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Calendar className="inline w-4 h-4 mr-2" />
            Harmonogram
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Ustawienia ogólne */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* AI Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Integracja AI</h3>
                
                <label className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                    <span className="text-white">Generator AI włączony</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={generalSettings.aiEnabled}
                    onChange={(e) => setGeneralSettings({...generalSettings, aiEnabled: e.target.checked})}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                </label>

                <label className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Hash className="w-5 h-5 text-blue-400" />
                    <span className="text-white">Auto-generowanie hashtagów</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={generalSettings.autoHashtags}
                    onChange={(e) => setGeneralSettings({...generalSettings, autoHashtags: e.target.checked})}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                </label>

                <label className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-green-400" />
                    <span className="text-white">Auto-tłumaczenie</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={generalSettings.autoTranslate}
                    onChange={(e) => setGeneralSettings({...generalSettings, autoTranslate: e.target.checked})}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                </label>
              </div>

              {/* Media Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Multimedia</h3>
                
                <label className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Image className="w-5 h-5 text-orange-400" />
                    <span className="text-white">Dodaj watermark</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={generalSettings.watermark}
                    onChange={(e) => setGeneralSettings({...generalSettings, watermark: e.target.checked})}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                </label>

                {generalSettings.watermark && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Tekst watermark
                    </label>
                    <input
                      type="text"
                      value={generalSettings.watermarkText}
                      onChange={(e) => setGeneralSettings({...generalSettings, watermarkText: e.target.value})}
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                      placeholder="@plexisystem"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Min. hashtagów
                    </label>
                    <input
                      type="number"
                      value={generalSettings.minHashtags}
                      onChange={(e) => setGeneralSettings({...generalSettings, minHashtags: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                      min="0"
                      max="30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Max. hashtagów
                    </label>
                    <input
                      type="number"
                      value={generalSettings.maxHashtags}
                      onChange={(e) => setGeneralSettings({...generalSettings, maxHashtags: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white"
                      min="0"
                      max="30"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* UTM Tracking */}
            <div className="mt-6 p-4 bg-zinc-900 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4">Śledzenie UTM</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    UTM Source
                  </label>
                  <input
                    type="text"
                    value={generalSettings.utmSource}
                    onChange={(e) => setGeneralSettings({...generalSettings, utmSource: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    UTM Medium
                  </label>
                  <input
                    type="text"
                    value={generalSettings.utmMedium}
                    onChange={(e) => setGeneralSettings({...generalSettings, utmMedium: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    UTM Campaign
                  </label>
                  <input
                    type="text"
                    value={generalSettings.utmCampaign}
                    onChange={(e) => setGeneralSettings({...generalSettings, utmCampaign: e.target.value})}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Szablony hashtagów */}
        {activeTab === 'hashtags' && (
          <div className="space-y-6">
            {/* Dodawanie nowego szablonu */}
            <div className="p-4 bg-zinc-900 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4">Dodaj szablon hashtagów</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  value={newHashtagTemplate.name}
                  onChange={(e) => setNewHashtagTemplate({...newHashtagTemplate, name: e.target.value})}
                  placeholder="Nazwa szablonu"
                  className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                />
                <input
                  type="text"
                  value={newHashtagTemplate.tags}
                  onChange={(e) => setNewHashtagTemplate({...newHashtagTemplate, tags: e.target.value})}
                  placeholder="Tagi (oddzielone przecinkami)"
                  className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                />
                <div className="flex gap-2">
                  <select
                    value={newHashtagTemplate.category}
                    onChange={(e) => setNewHashtagTemplate({...newHashtagTemplate, category: e.target.value})}
                    className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  >
                    <option value="Produkty">Produkty</option>
                    <option value="Biznes">Biznes</option>
                    <option value="Wydarzenia">Wydarzenia</option>
                    <option value="Inne">Inne</option>
                  </select>
                  <button
                    onClick={addHashtagTemplate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Lista szablonów */}
            <div className="space-y-3">
              {hashtagTemplates.map(template => (
                <div key={template.id} className="p-4 bg-zinc-900 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-semibold text-white">{template.name}</h4>
                      <span className="text-sm text-gray-400">{template.category}</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const tags = prompt('Edytuj tagi (oddzielone przecinkami):', template.tags.join(', '));
                          if (tags) {
                            setHashtagTemplates(hashtagTemplates.map(t => 
                              t.id === template.id 
                                ? {...t, tags: tags.split(',').map(tag => tag.trim())}
                                : t
                            ));
                          }
                        }}
                        className="p-2 text-blue-400 hover:bg-zinc-800 rounded-lg transition-all"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteHashtagTemplate(template.id)}
                        className="p-2 text-red-400 hover:bg-zinc-800 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {template.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Szablony postów */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Dodaj szablon posta
            </button>

            <div className="space-y-4">
              {postTemplates.map(template => (
                <div key={template.id} className="p-4 bg-zinc-900 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-semibold text-white">{template.name}</h4>
                    <div className="flex gap-2">
                      <button className="p-2 text-blue-400 hover:bg-zinc-800 rounded-lg transition-all">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-red-400 hover:bg-zinc-800 rounded-lg transition-all">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-zinc-800 rounded-lg mb-3">
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap font-sans">
                      {template.content}
                    </pre>
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex gap-2">
                      {template.platforms.map(platform => (
                        <span key={platform} className="text-gray-400">
                          {getPlatformIcon(platform)}
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      {template.hashtags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-blue-400">#{tag}</span>
                      ))}
                      {template.hashtags.length > 3 && (
                        <span className="text-gray-500">+{template.hashtags.length - 3}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ustawienia platform */}
        {activeTab === 'platforms' && (
          <div className="space-y-6">
            {platformSettings.map((platform, index) => (
              <div key={platform.platform} className="p-4 bg-zinc-900 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getPlatformIcon(platform.platform)}
                    <h4 className="font-semibold text-white text-lg">{platform.platform}</h4>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={platform.enabled}
                      onChange={(e) => {
                        const updated = [...platformSettings];
                        updated[index].enabled = e.target.checked;
                        setPlatformSettings(updated);
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {platform.enabled && (
                  <div className="space-y-4">
                    {/* Domyślne hashtagi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Domyślne hashtagi
                      </label>
                      <input
                        type="text"
                        value={platform.defaultHashtags.join(', ')}
                        onChange={(e) => {
                          const updated = [...platformSettings];
                          updated[index].defaultHashtags = e.target.value.split(',').map(t => t.trim());
                          setPlatformSettings(updated);
                        }}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                        placeholder="hashtag1, hashtag2, hashtag3"
                      />
                    </div>

                    {/* Godziny publikacji */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Optymalne godziny publikacji
                      </label>
                      <div className="flex gap-2">
                        {platform.postingTimes.map((time, timeIndex) => (
                          <input
                            key={timeIndex}
                            type="time"
                            value={time}
                            onChange={(e) => {
                              const updated = [...platformSettings];
                              updated[index].postingTimes[timeIndex] = e.target.value;
                              setPlatformSettings(updated);
                            }}
                            className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                          />
                        ))}
                        <button className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-gray-400 hover:text-white">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Auto-publikacja */}
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={platform.autoPost}
                        onChange={(e) => {
                          const updated = [...platformSettings];
                          updated[index].autoPost = e.target.checked;
                          setPlatformSettings(updated);
                        }}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-white">Włącz auto-publikację</span>
                    </label>

                    {/* Połączenie API */}
                    {(platform.platform === 'Facebook' || platform.platform === 'LinkedIn' || platform.platform === 'Twitter') && (
                      <div className={`p-3 rounded-lg border ${
                        connectedPlatforms[platform.platform]
                          ? 'bg-green-900/20 border-green-600/30'
                          : 'bg-yellow-900/20 border-yellow-600/30'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          {connectedPlatforms[platform.platform] ? (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-400" />
                              <span className="text-sm font-medium text-green-400">Konto połączone</span>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-4 h-4 text-yellow-400" />
                              <span className="text-sm font-medium text-yellow-400">Wymagane połączenie API</span>
                            </>
                          )}
                        </div>
                        {connectedPlatforms[platform.platform] ? (
                          <div className="flex gap-2">
                            <button 
                              onClick={() => {
                                // Odłącz konto
                                const tokens = JSON.parse(localStorage.getItem('social_tokens') || '{}');
                                delete tokens[platform.platform.toLowerCase()];
                                localStorage.setItem('social_tokens', JSON.stringify(tokens));
                                checkConnectedPlatforms();
                              }}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-sm">
                              Odłącz konto
                            </button>
                            <button 
                              onClick={() => handleConnectPlatform(platform.platform)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm">
                              Połącz ponownie
                            </button>
                          </div>
                        ) : (
                          <button 
                            onClick={() => handleConnectPlatform(platform.platform)}
                            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-all text-sm">
                            <Link2 className="inline w-4 h-4 mr-2" />
                            Połącz konto {platform.platform}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Harmonogram */}
        {activeTab === 'schedule' && (
          <div className="space-y-6">
            <div className="p-6 bg-zinc-900 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4">Harmonogram publikacji</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Częstotliwość postów</h4>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3">
                      <input type="radio" name="frequency" className="text-blue-600" defaultChecked />
                      <span className="text-white">Codziennie</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input type="radio" name="frequency" className="text-blue-600" />
                      <span className="text-white">3-4 razy w tygodniu</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input type="radio" name="frequency" className="text-blue-600" />
                      <span className="text-white">1-2 razy w tygodniu</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input type="radio" name="frequency" className="text-blue-600" />
                      <span className="text-white">Własny harmonogram</span>
                    </label>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Najlepsze dni</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Ndz'].map(day => (
                      <label key={day} className="flex items-center gap-2">
                        <input type="checkbox" className="text-blue-600" defaultChecked={day !== 'Sob' && day !== 'Ndz'} />
                        <span className="text-white text-sm">{day}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-600/20 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-blue-400 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Optymalne czasy publikacji</span>
                </div>
                <p className="text-sm text-gray-300">
                  Na podstawie analizy Twojej grupy docelowej, najlepsze godziny to:
                  <span className="font-semibold text-white ml-1">8:00-9:00, 12:00-13:00, 17:00-19:00</span>
                </p>
              </div>
            </div>

            <div className="p-6 bg-zinc-900 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4">Kolejka publikacji</h3>
              <p className="text-gray-400">Brak zaplanowanych postów</p>
              <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all">
                <Plus className="inline w-4 h-4 mr-2" />
                Zaplanuj pierwszy post
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialMediaSettings;
